import { Box, Grid, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import SecondaryButton from "../../../../components/buttons/SecondaryButton";
import LoadingIcon from "../../../../components/LoadingIcon";
import { Toast } from "../../../../components/Toast";
import Guideline from "../components/Guideline";
import GuidelineStep from "../components/GuidelineStep";
import GuidelineStepByStep from "../components/GuidelineStepByStep";
import splunk_cloud_step_1 from "./images/splunk_cloud_step_1.png";
import splunk_cloud_step_2 from "./images/splunk_cloud_step_2.png";
import splunk_cloud_step_3 from "./images/splunk_cloud_step_3.png";
import splunk_cloud_step_4 from "./images/splunk_cloud_step_4.png";
import splunk_cloud_step_5 from "./images/splunk_cloud_step_5.png";
import splunk_cloud_step_6 from "./images/splunk_cloud_step_6.png";
import splunk_cloud_step_7 from "./images/splunk_cloud_step_7.png";
import splunk_cloud_step_8 from "./images/splunk_cloud_step_8.png";

import { useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import useAPI from "../../../../api/hooks/useAPI";
import useIsIntegrated from "../../../../api/hooks/useIsIntegrated";
import { integrationKeys } from "../../../../api/queryKeys";
import useExpandedList from "../../../../components/hooks/ExpandedList";
import { ScreenshotAccordion } from "../../../../components/ScreenshotAccordion";
import { logos } from "../../environment/icons/iconExport";
import BreezeStaticIPsModal from "../components/BreezeStaticIPsModal";
import IntegrationGuideAlert from "../components/IntegrationGuideAlert";
import WebhookConfig from "../components/WebhookConfig";
import { IntegrationsMapping } from "../IntegrationsMapping";
import SplunkCloudConnectForm from "./SplunkCloudConnectForm";

const INTEGRATION_CODE_NAME = "splunk_cloud";

const SplunkCloudIntegration = () => {
  const integration = IntegrationsMapping[INTEGRATION_CODE_NAME];
  const [searchParams] = useSearchParams();
  const [guideMode, setGuideMode] = useState(false);
  const [showStaticIPsModal, setShowStaticIPsModal] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (searchParams.has("viewonly")) {
      setGuideMode(true);
    }
  }, [searchParams]);

  const { isIntegrated, isIntegratedLoading } = useIsIntegrated(
    integration.codeName
  );
  const [loading, setLoading] = useState(false);

  const { addToExpandedList, handleExpandAll, handleCollapseAll } =
    useExpandedList();
  const { registerSplunkCloudIntegration } = useAPI();

  const onSubmit = (values) => {
    setLoading(true);
    registerSplunkCloudIntegration(
      values.webhookURL.trim(),
      values.apiToken.trim(),
      values.tokenIndex.trim(),
      values.port.trim()
    )
      .then((response) => {
        queryClient.invalidateQueries({
          queryKey: integrationKeys.all,
        });
        Toast.success(`${integration.displayName} integrated successfully`);
      })
      .catch((error) => {
        if (error.response) {
          Toast.error(`Something went wrong: ${error.response.data.detail}`);
        } else {
          Toast.error("An error occurred while processing the request.");
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Box>
      <Guideline
        title={
          isIntegrated
            ? ""
            : `Once integrated, the Breeze platform will send alerts about new detections to the ${integration.displayName} HTTP Event Collector.`
        }
        image={logos.SplunkCloudLogo}
        iconBorderRadius={0}
        name={integration.displayName}
        content={
          <Box>
            {(loading || isIntegratedLoading) && <LoadingIcon size={25} />}
            {isIntegrated && !guideMode && (
              <WebhookConfig
                toolName={integration.codeName}
                toolDisplayName={integration.displayName}
              />
            )}

            <Box
              sx={{
                display:
                  loading || (isIntegrated && !guideMode) ? "none" : "block",
              }}
            >
              {/* BUTTONS TO CONTROL EXPANSION */}
              <Box mb="20px">
                <Grid container spacing={2} direction={"row"}>
                  {guideMode && <IntegrationGuideAlert />}

                  <Grid item>
                    <SecondaryButton onClick={handleExpandAll}>
                      Expand All Screenshots
                    </SecondaryButton>
                  </Grid>
                  <Grid item>
                    <SecondaryButton onClick={handleCollapseAll}>
                      Collapse All Screenshots
                    </SecondaryButton>
                  </Grid>
                  <Grid item>
                    <SecondaryButton
                      onClick={() => setShowStaticIPsModal(true)}
                    >
                      View Breeze Static IPs
                    </SecondaryButton>
                  </Grid>
                </Grid>
              </Box>

              {/* SETUP INSTRUCTIONS */}
              <GuidelineStepByStep>
                <GuidelineStep title="Login to Splunk Cloud">
                  <Typography variant={"body2"}>
                    Sign in to your Splunk Cloud console as an administrator.
                  </Typography>
                </GuidelineStep>
                <GuidelineStep title="Generate API Key">
                  <Typography variant={"body2"}>
                    Navigate to <b> Settings {">"} Data inputs</b>.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: splunk_cloud_step_1,
                        altext: "Navigate to Settings > Data inputs",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant={"body2"}>
                    Navigate to <b> HTTP Event Collector</b>.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: splunk_cloud_step_2,
                        altext: "Navigate to HTTP Event Collector",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />

                  <Typography variant={"body2"}>
                    Click<b> New Token</b>.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: splunk_cloud_step_3,
                        altext: "Click New Token",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant={"body2"}>
                    Choose a name for the token and make sure to select the{" "}
                    <b>HTTP Event Collector</b>.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: splunk_cloud_step_4,
                        altext: "Select the HTTP Event Collector",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant={"body2"}>
                    Add all indexes or selected set of indexes, and leave source
                    type = <b>Automatic</b>.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: splunk_cloud_step_5,
                        altext: "Select indexes",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant={"body2"}>
                    Create the token, navigate back to{" "}
                    <b>
                      {" "}
                      Settings {">"} Data inputs {">"} HTTP Event Collector
                    </b>{" "}
                    and copy its value.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: splunk_cloud_step_6,
                        altext: "Copy token value",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant={"body2"}>
                    Paste the Token value in the form below.
                  </Typography>
                </GuidelineStep>
                <GuidelineStep title="Find Your Splunk Cloud URL">
                  <Typography variant={"body2"}>
                    Your Splunk Cloud URL is the URL for the HTTP Event
                    Collector (HEC) Webhook.
                  </Typography>
                  <Typography variant={"body2"}>
                    It should look like one of the following: <br />
                    <br />
                    <code
                      style={{
                        backgroundColor: "#f5f5f5",
                        padding: "2px 4px",
                        borderRadius: "4px",
                      }}
                    >
                      https://
                      <b>
                        {"<"}splunk-cloud-host{">"}
                      </b>
                      .splunkcloud.com
                    </code>
                    <br />
                    <code
                      style={{
                        backgroundColor: "#f5f5f5",
                        padding: "2px 4px",
                        borderRadius: "4px",
                      }}
                    >
                      https://
                      <b>
                        {"<"}splunk-cloud-host{">"}
                      </b>
                      .cloud.splunk.com
                    </code>
                    <br />
                    <code
                      style={{
                        backgroundColor: "#f5f5f5",
                        padding: "2px 4px",
                        borderRadius: "4px",
                      }}
                    >
                      https://input-
                      <b>
                        {"<"}splunk-cloud-host{">"}
                      </b>
                      .splunkcloud.com
                    </code>
                    <br />
                    <br />
                  </Typography>
                  <Typography variant={"body2"}>
                    Copy the Splunk Cloud URL and paste it in the form below.
                  </Typography>
                </GuidelineStep>
                <GuidelineStep title="Find HTTP port">
                  <Typography variant={"body2"}>
                    Navigate to{" "}
                    <b>
                      {" "}
                      Settings {">"} Data inputs {">"} HTTP Event Collector
                    </b>{" "}
                    and click on <b>Global Settings</b>
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: splunk_cloud_step_7,
                        altext: "Click on Global Settings",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant={"body2"}>
                    Copy your port number and paste it in the form below.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: splunk_cloud_step_8,
                        altext: "Copy port number",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                </GuidelineStep>
                <GuidelineStep
                  id="connectSplunkCloud"
                  title="Connect Splunk Cloud"
                >
                  <Box width={700}>
                    <SplunkCloudConnectForm
                      onSubmit={onSubmit}
                      guideMode={guideMode}
                    />
                  </Box>
                </GuidelineStep>
              </GuidelineStepByStep>
            </Box>
          </Box>
        }
      />
      <BreezeStaticIPsModal
        open={showStaticIPsModal}
        onClose={() => setShowStaticIPsModal(false)}
      />
    </Box>
  );
};

export default SplunkCloudIntegration;
